import { z } from "zod";
import type { Kpi, ServiceTemplate } from "../../types";
import { Schemas } from "../../schemas";
import { sigv4ApiClient } from "./internal/sigv4ApiClient";
import { createEntityId } from "./internal/entityId";

const API_PATH = "/v1/kpi";
const SERVICE_TEMPLATE_API_PATH = "/v1/service-template";

const schema = z.array(Schemas.kpi);
const serviceTemplatesSchema = z.array(Schemas.serviceTemplate);

const loadAll = async (): Promise<Kpi[]> =>
  schema.parse(await sigv4ApiClient.list<Kpi>(API_PATH));

const saveOne = async (kpi: Kpi): Promise<Kpi> =>
  sigv4ApiClient.put<Kpi>(API_PATH, kpi);

const loadStoredServiceTemplates = async (): Promise<ServiceTemplate[]> =>
  serviceTemplatesSchema.parse(
    await sigv4ApiClient.list<ServiceTemplate>(SERVICE_TEMPLATE_API_PATH),
  );

export const kpiService = {
  list: async (): Promise<Kpi[]> => {
    return loadAll();
  },
  create: async (
    input: Pick<Kpi, "name" | "description" | "thresholds">,
  ): Promise<Kpi> => {
    const id = createEntityId();

    const created: Kpi = Schemas.kpi.parse({
      id,
      name: input.name,
      description: input.description ?? "",
      thresholds: input.thresholds,
      alerts: [],
      alertConnections: [],
      weight: 0,
      metrics: {},
    });

    return saveOne(created);
  },
  get: async (id: string): Promise<Kpi | undefined> => {
    const all = await kpiService.list();
    return all.find((kpi) => kpi.id === id);
  },
  patch: async (
    id: string,
    patch: Partial<Pick<Kpi, "name" | "description" | "thresholds">>,
  ): Promise<Kpi> => {
    const kpis = await loadAll();
    const idx = kpis.findIndex((kpi) => kpi.id === id);

    if (idx === -1) {
      throw new Error(`KPI with id "${id}" not found`);
    }

    const current = kpis[idx];
    if (current.system) {
      throw new Error("System KPIs cannot be edited.");
    }
    const updated = Schemas.kpi.parse({
      ...current,
      ...patch,
      id,
      name: patch.name?.trim() ?? current.name,
      description: patch.description?.trim() ?? current.description ?? "",
      thresholds: patch.thresholds
        ? {
            warning: patch.thresholds.warning,
            critical: patch.thresholds.critical,
          }
        : current.thresholds,
    });

    return saveOne(updated);
  },
  delete: async (id: string): Promise<boolean> => {
    const kpis = await loadAll();
    const target = kpis.find((kpi) => kpi.id === id);

    if (!target) {
      return false;
    }

    if (target.system) {
      return false;
    }

    const templates = await loadStoredServiceTemplates();
    const linkedTemplates = templates
      .filter((template) => template.kpis.some((kpi) => kpi.id === id))
      .map((template) => template.name);

    if (linkedTemplates.length > 0) {
      throw new Error(
        `KPI is used by service templates: ${linkedTemplates.join(", ")}. Remove it from templates first.`,
      );
    }

    await sigv4ApiClient.delete(`${API_PATH}/${encodeURIComponent(id)}`);
    return true;
  },
  isDefaultId: (): boolean => {
    return false;
  },
};
